import {
  CheckCircle,
  XCircle,
  MessageSquare,
  Clock,
  type LucideIcon,
} from "lucide-react";
import type { BookingLifecycleStatus, HandshakeSessionStatus } from "@/types";
import type { BadgeVariant } from "@/components/ui/badge-variants";
import {
  partnerDisplayStatus,
  type PartnerDisplayStatus,
} from "./sellerBookingRules";

export interface BookingStatusConfig {
  labelKey: string;
  icon: LucideIcon;
  color: string;
  bgColor: string;
  badgeVariant: BadgeVariant;
}

/** Partner list/card presentation layered on badge config. */
export interface PartnerStatusConfig extends BookingStatusConfig {
  cardBorderClass: string;
  badgeRingClass?: string;
  /** Keep full badge color on busy list cards. */
  emphasizeBadge: boolean;
  /** Dim title/time and soften the card surface. */
  settleCard: boolean;
  /** Soft tint on the card body (e.g. pending handshake). */
  highlightCard?: boolean;
}

/** Labels and styling for each backend BookingStatus value. */
export const BOOKING_STATUS_CONFIG: Record<
  BookingLifecycleStatus,
  BookingStatusConfig
> = {
  CREATED: {
    labelKey: "status.CREATED",
    icon: Clock,
    color: "text-surface-600",
    bgColor: "bg-surface-100",
    badgeVariant: "default",
  },
  PENDING_HANDSHAKE: {
    labelKey: "status.PENDING_HANDSHAKE",
    icon: MessageSquare,
    color: "text-accent-700",
    bgColor: "bg-accent-50",
    badgeVariant: "accent",
  },
  ACCEPTED: {
    labelKey: "status.ACCEPTED",
    icon: CheckCircle,
    color: "text-success-700",
    bgColor: "bg-success-50",
    badgeVariant: "success",
  },
  REJECTED: {
    labelKey: "status.REJECTED",
    icon: XCircle,
    color: "text-error-700",
    bgColor: "bg-error-50",
    badgeVariant: "error",
  },
  COMPLETED: {
    labelKey: "status.COMPLETED",
    icon: CheckCircle,
    color: "text-success-700",
    bgColor: "bg-success-50",
    badgeVariant: "success",
  },
  CANCELED: {
    labelKey: "status.CANCELED",
    icon: XCircle,
    color: "text-error-700",
    bgColor: "bg-error-50",
    badgeVariant: "error",
  },
  ABANDONED: {
    labelKey: "status.ABANDONED",
    icon: Clock,
    color: "text-warning-700",
    bgColor: "bg-warning-50",
    badgeVariant: "warning",
  },
};

export function bookingStatusConfig(
  status: BookingLifecycleStatus,
): BookingStatusConfig {
  return BOOKING_STATUS_CONFIG[status];
}

/** Partner-facing status — badge + list/card tone in one place. */
export const PARTNER_DISPLAY_STATUS_CONFIG: Record<
  PartnerDisplayStatus,
  PartnerStatusConfig
> = {
  created: {
    ...BOOKING_STATUS_CONFIG.CREATED,
    cardBorderClass: "border-l-surface-400",
    emphasizeBadge: false,
    settleCard: false,
  },
  pending: {
    labelKey: "partnerStatus.pending",
    icon: MessageSquare,
    color: "text-accent-700",
    bgColor: "bg-accent-50",
    badgeVariant: "accent",
    cardBorderClass: "border-l-accent-600",
    emphasizeBadge: true,
    settleCard: false,
    highlightCard: true,
  },
  confirmed: {
    labelKey: "partnerStatus.confirmed",
    icon: CheckCircle,
    color: "text-success-700",
    bgColor: "bg-success-50",
    badgeVariant: "success",
    cardBorderClass: "border-l-success-600",
    emphasizeBadge: true,
    settleCard: false,
  },
  completed: {
    ...BOOKING_STATUS_CONFIG.COMPLETED,
    cardBorderClass: "border-l-success-500",
    emphasizeBadge: true,
    settleCard: true,
  },
  cancelled: {
    labelKey: "status.CANCELED",
    icon: XCircle,
    color: "text-error-700",
    bgColor: "bg-error-50",
    badgeVariant: "error",
    cardBorderClass: "border-l-error-500",
    emphasizeBadge: true,
    settleCard: true,
  },
  abandoned: {
    labelKey: "status.ABANDONED",
    icon: Clock,
    color: "text-warning-700",
    bgColor: "bg-warning-50",
    badgeVariant: "warning",
    cardBorderClass: "border-l-warning-500",
    badgeRingClass: "ring-warning-200/80",
    emphasizeBadge: true,
    settleCard: true,
  },
  rejected: {
    labelKey: "status.REJECTED",
    icon: XCircle,
    color: "text-error-700",
    bgColor: "bg-error-50",
    badgeVariant: "error",
    cardBorderClass: "border-l-error-500",
    emphasizeBadge: true,
    settleCard: true,
  },
};

export function partnerBookingStatusConfig(
  status: BookingLifecycleStatus,
  handshakeStatus: HandshakeSessionStatus | null,
): PartnerStatusConfig {
  return PARTNER_DISPLAY_STATUS_CONFIG[
    partnerDisplayStatus(status, handshakeStatus)
  ];
}
